import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';

interface UserProfileData {
    display_name: string;
    phoneNumber: string;
    postalCode: string;
    address: string;
}

export default function UserProfile() {
    const navigate = useNavigate();
    const { userId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [profileData, setProfileData] = useState<UserProfileData>({
        display_name: '',
        phoneNumber: '',
        postalCode: '',
        address: ''
    });
    const [editForm, setEditForm] = useState<UserProfileData>({
        display_name: '',
        phoneNumber: '',
        postalCode: '',
        address: ''
    });

    useEffect(() => {
        if (userId) {
            fetchUserProfile();
        }
    }, [userId]);

    const fetchUserProfile = async () => {
        if (!userId) return;
        try {
            const userDoc = await getDoc(doc(db, 'User', userId));
            if (userDoc.exists()) {
                const data = userDoc.data();
                const userData = {
                    display_name: data.display_name || '',
                    phoneNumber: data.phoneNumber || '',
                    postalCode: data.postalCode || '',
                    address: data.address || ''
                };
                setProfileData(userData);
                setEditForm(userData);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
            alert('ユーザー情報の取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!userId) return;

        // Basic validation
        if (!editForm.postalCode || !editForm.address) {
            alert('郵便番号と住所は必須です');
            return;
        }

        try {
            setLoading(true);
            await updateDoc(doc(db, 'User', userId), {
                postalCode: editForm.postalCode,
                address: editForm.address
                // display_name and phoneNumber are read-only and not updated
            });

            setProfileData(editForm);
            setIsEditing(false);
            alert('プロフィールを更新しました');
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('更新に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    if (loading && !profileData.display_name) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">読み込み中...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F5F5]">
            <div className="max-w-md mx-auto px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-lg font-bold text-gray-800">マイページ</h1>
                    <button
                        onClick={() => navigate('/home')}
                        className="px-4 py-2 bg-white text-gray-600 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-sm font-bold shadow-sm"
                    >
                        ホームに戻る
                    </button>
                </div>

                <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                            <i className="ri-user-line text-orange-500"></i>
                            基本情報
                        </h2>
                        {!isEditing && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-2 bg-[#6B96C3] text-white rounded-xl hover:bg-[#5A85B2] transition-colors text-sm font-bold shadow-sm"
                            >
                                編集する
                            </button>
                        )}
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Display Name */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1.5">お名前</label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editForm.display_name}
                                    disabled
                                    className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-not-allowed"
                                />
                            ) : (
                                <p className="text-base font-medium text-gray-800">{profileData.display_name || '未設定'}</p>
                            )}
                        </div>

                        {/* Phone Number */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 mb-1.5">電話番号</label>
                            {isEditing ? (
                                <input
                                    type="tel"
                                    value={editForm.phoneNumber}
                                    disabled
                                    className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-not-allowed"
                                />
                            ) : (
                                <p className="text-base font-medium text-gray-800">{profileData.phoneNumber || '未設定'}</p>
                            )}
                        </div>

                        <div className="border-t border-gray-100 pt-6">
                            <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <i className="ri-map-pin-line text-orange-500"></i>
                                住所情報
                            </h3>

                            {/* Postal Code */}
                            <div className="mb-4">
                                <label className="block text-xs font-bold text-gray-500 mb-1.5">郵便番号</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        placeholder="123-4567"
                                        value={editForm.postalCode}
                                        onChange={(e) => setEditForm({ ...editForm, postalCode: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all text-sm"
                                    />
                                ) : (
                                    <p className="text-base font-medium text-gray-800">{profileData.postalCode || '未設定'}</p>
                                )}
                            </div>

                            {/* Address */}
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1.5">住所</label>
                                {isEditing ? (
                                    <textarea
                                        rows={3}
                                        placeholder="都道府県 市区町村 番地 建物名など"
                                        value={editForm.address}
                                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all text-sm resize-none"
                                    />
                                ) : (
                                    <p className="text-base font-medium text-gray-800 whitespace-pre-wrap leading-relaxed">{profileData.address || '未設定'}</p>
                                )}
                            </div>
                        </div>

                        {isEditing && (
                            <div className="flex gap-3 pt-4 border-t border-gray-100">
                                <button
                                    onClick={() => {
                                        setIsEditing(false);
                                        setEditForm(profileData);
                                    }}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition-colors text-sm"
                                >
                                    キャンセル
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="flex-1 px-4 py-3 bg-[#F4B183] text-white rounded-xl font-bold hover:bg-[#E5A072] transition-colors text-sm shadow-sm"
                                >
                                    保存する
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
